import type {FC} from 'react'
import {Box, Button, CircularProgress, Typography} from '@mui/material'
import ReplayIcon from '@mui/icons-material/Replay'
import type {Message, ToolSummaryContent} from '@interfaces/alpinist/message'
import type {UserContextProps} from '@containers/App/UserProvider'
import {formatDateTime} from '@shared/utils/dateUtils'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import AgentBubble from '@containers/Dashboards/AgentChat/components/messages/AgentBubble'
import ErrorBubble from '@containers/Dashboards/AgentChat/components/messages/ErrorBubble'
import MemoryConfirmationPrompt from '@containers/Dashboards/AgentChat/components/messages/MemoryConfirmationPrompt'
import MemoryWriteNoticeCard from '@containers/Dashboards/AgentChat/components/messages/MemoryWriteNoticeCard'
import MessageFeedbackActions from '@containers/Dashboards/AgentChat/components/messages/MessageFeedbackActions'
import SystemBubble from '@containers/Dashboards/AgentChat/components/messages/SystemBubble'
import ToolBubble from '@containers/Dashboards/AgentChat/components/messages/ToolBubble'
import UserAttachments from '@containers/Dashboards/AgentChat/components/messages/UserAttachments'
import ImageThumbnail from '@containers/Dashboards/AgentChat/components/messages/ImageThumbnail'
import {useBoundCaptures} from '@shared/alpinist/screenCapture/liveCaptures'
import UserBubble from '@containers/Dashboards/AgentChat/components/messages/UserBubble'
import DeliveryNote from '@containers/Dashboards/AgentChat/components/messages/DeliveryNote'
import StoppedNotice from '@containers/Dashboards/AgentChat/components/messages/StoppedNotice'
import type {DeliveryLabel} from '@containers/Dashboards/AgentChat/components/conversationRun/runState'
import MarkdownText from '@containers/Dashboards/AgentChat/components/messages/markdown/MarkdownText'
import {stripReasoningText} from '@containers/Dashboards/AgentChat/components/chatMessageList/stripReasoningText'
import {errorMessageKey} from '@containers/Dashboards/AgentChat/components/chatMessageList/errorMessage'

interface KbRefTarget {
  documentId: string
  page?: number
}

interface ChatMessageRowProps {
  message: Message
  messageIndex: number
  conversationId: string
  tone: 'default' | 'incident'
  showAuthors: boolean
  currentUser: UserContextProps['currentUser']
  onKbRefClick: (ref: KbRefTarget) => void
  getActorLabel: (message: Message) => string | null
  isActorLoading: (message: Message) => boolean
  /** When set, an error row shows a Retry button that re-sends the failed turn. */
  onRetry?: () => void
  retrying?: boolean
  /** How a user message sent while Alpinist worked reached it (AB#6760). */
  delivery?: DeliveryLabel | null
}

/**
 * The screen captured during this answer's turn, while the answer's live event
 * came without it as an attachment (AB#6762). Gone on reload, where the
 * persisted attachment shows instead.
 */
const BoundCaptures: FC<{answerId: string}> = ({answerId}) => {
  const [t] = useCustomTranslation('alpinist')
  const captures = useBoundCaptures(answerId)
  if (!captures.length) return null
  return (
    <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1}}>
      {captures.map((capture) => (
        <ImageThumbnail key={capture.requestId} src={capture.url} alt={t('screen_capture_alt')} />
      ))}
    </Box>
  )
}

const ChatMessageRow: FC<ChatMessageRowProps> = ({
  message,
  messageIndex,
  conversationId,
  tone,
  showAuthors,
  currentUser,
  onKbRefClick,
  getActorLabel,
  isActorLoading,
  onRetry,
  retrying = false,
  delivery = null,
}) => {
  const [t] = useCustomTranslation('alpinist')
  const key = message.id ?? `${message.role}-${messageIndex}-${message.created}`
  if (!message.content) return null

  if (message.content.type === 'tools') {
    return (
      <ToolBubble key={key} content={message.content as ToolSummaryContent} final tone={tone} />
    )
  }

  const text = message.content.text
  const visibleText = message.role === 'agent' ? stripReasoningText(text) : text

  switch (message.role) {
    case 'system':
      return (
        <SystemBubble key={key}>
          <Typography
            variant="body2"
            sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4}}
          >
            {text}
          </Typography>
        </SystemBubble>
      )

    case 'error':
      return (
        <ErrorBubble key={key}>
          <Typography
            variant="body2"
            sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.2}}
          >
            {t(errorMessageKey(visibleText))}
          </Typography>
          {/* The raw code stays visible: support can act on a screenshot, and a code
              this build has no sentence for is still readable. */}
          <Typography variant="caption" sx={{display: 'block', mt: 0.5, opacity: 0.7}}>
            {visibleText}
          </Typography>
          {onRetry ? (
            <Box sx={{mt: 0.75}}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={onRetry}
                disabled={retrying}
                startIcon={
                  retrying ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />
                }
              >
                {t('chat_retry')}
              </Button>
            </Box>
          ) : null}
        </ErrorBubble>
      )

    case 'user':
      return (
        <Box
          key={key}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.35,
          }}
        >
          {showAuthors ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mr: 0.5,
                '& .incident-chat-timestamp': {
                  opacity: 0,
                  transition: 'opacity 150ms ease',
                },
                '&:hover .incident-chat-timestamp': {
                  opacity: 1,
                },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                className="incident-chat-timestamp"
              >
                {formatDateTime(message.created, currentUser)}
              </Typography>
              {isActorLoading(message) ? (
                <CircularProgress size={12} />
              ) : getActorLabel(message) ? (
                <Typography variant="caption" color="text.secondary">
                  {getActorLabel(message)}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {message.attachments?.length ? (
            <UserAttachments conversationId={conversationId} attachments={message.attachments} />
          ) : null}

          {visibleText ? (
            <UserBubble muted={delivery === 'withdrawn'}>
              <Typography
                variant="body2"
                sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.2}}
              >
                {visibleText}
              </Typography>
            </UserBubble>
          ) : null}

          {delivery ? <DeliveryNote label={delivery} /> : null}
        </Box>
      )

    case 'agent':
    default:
      return (
        <AgentBubble key={key} tone={tone}>
          {message.content.stopped ? (
            <StoppedNotice
              writes={message.content.completed_writes}
              mode={message.content.stop_mode}
            />
          ) : null}
          {/* The screen Alpinist looked at this turn (AB#6762), above its answer. */}
          {message.attachments?.length ? (
            <Box sx={{mb: 1}}>
              <UserAttachments
                conversationId={conversationId}
                attachments={message.attachments}
                align="start"
              />
            </Box>
          ) : (
            <BoundCaptures answerId={String(message.id)} />
          )}
          <MarkdownText text={visibleText} onKbRefClick={onKbRefClick} />
          <MessageFeedbackActions
            conversationId={conversationId}
            messageId={String(message.id)}
            initialRating={message.my_feedback?.rating ?? null}
            initialDetails={message.my_feedback?.details ?? null}
          />
          {/* What this turn WROTE comes first, then what it asks the reader to
              confirm. They are different gestures on different claims -- "your words
              are now shared" versus "does a colleague's claim hold?" -- and the
              write is the one with a consequence the person did not ask for. */}
          {message.memory_writes && message.memory_writes.length > 0 && (
            <MemoryWriteNoticeCard notices={message.memory_writes} />
          )}
          {message.memory_ask && (
            <MemoryConfirmationPrompt
              conversationId={conversationId}
              messageId={String(message.id)}
              ask={message.memory_ask}
            />
          )}
        </AgentBubble>
      )
  }
}

export default ChatMessageRow
